/**
 * Error taxonomy: classify errors for consistent handling across pane + custom functions.
 * All call sites must go through classifyError—never reach into proxy shapes directly.
 */

export type ErrorKind = "plan_limit" | "network" | "auth" | "unknown";

export interface PlanLimitInfo {
  title: string;
  message: string;
  limit_key?: string;
  limit_value?: number | string;
  effective_plan?: string;
  upgrade_url?: string;
  manage_url?: string;
  request_id?: string;
  action?: "upgrade" | "manage" | "none";
  source?: "taskpane_precheck" | "proxy" | "unknown";
}

/** Extract limit_key from proxy error for human-readable display. */
function limitKeyToLabel(key: string): string {
  const labels: Record<string, string> = {
    max_chain_rows: "chain rows",
    max_refresh_items_per_run: "refresh items",
    max_requests_per_minute: "requests per minute",
  };
  return labels[key] ?? key;
}

/** Build PlanLimitInfo from proxy error shape. Reads optional fields when present. */
export function normalizeToPlanLimitInfo(
  err: unknown,
  _status?: number,
  source?: PlanLimitInfo["source"]
): PlanLimitInfo {
  const fallback: PlanLimitInfo = {
    title: "Plan limit reached",
    message: "Upgrade to Pro for higher limits.",
    source: source ?? "unknown",
  };

  if (!err || typeof err !== "object") return fallback;

  const asRecord = err as Record<string, unknown>;
  const obj =
    asRecord.details && typeof asRecord.details === "object" && asRecord.code === "PLAN_LIMIT"
      ? (asRecord.details as Record<string, unknown>)
      : asRecord;

  const code = typeof asRecord.code === "string" ? asRecord.code : typeof obj.code === "string" ? obj.code : "";
  if (code !== "PLAN_LIMIT") return fallback;

  const message =
    typeof asRecord.message === "string"
      ? asRecord.message
      : typeof obj.message === "string"
        ? obj.message
        : fallback.message;
  const request_id =
    typeof obj.request_id === "string"
      ? obj.request_id
      : typeof asRecord.requestId === "string"
        ? asRecord.requestId
        : typeof (obj as { requestId?: string }).requestId === "string"
          ? (obj as { requestId: string }).requestId
          : undefined;

  const limit_key = typeof obj.limit_key === "string" ? obj.limit_key : undefined;
  const limit_value =
    typeof obj.limit_value === "number" || typeof obj.limit_value === "string"
      ? obj.limit_value
      : undefined;
  const effective_plan = typeof obj.effective_plan === "string" ? obj.effective_plan : undefined;
  const action =
    obj.action === "upgrade" || obj.action === "manage" || obj.action === "none"
      ? obj.action
      : undefined;

  const effectivePlanLabel = effective_plan === "pro" ? "Pro" : effective_plan === "free" ? "Free" : effective_plan;
  const limitLabel = limit_key ? limitKeyToLabel(limit_key) : undefined;
  const title =
    effectivePlanLabel && limitLabel && limit_value != null
      ? effectivePlanLabel + " plan limit (" + limitLabel + ": " + limit_value + ")"
      : "Plan limit reached";

  return {
    title,
    message,
    limit_key,
    limit_value,
    effective_plan,
    request_id,
    action: action ?? "upgrade",
    source: source ?? "proxy",
  };
}

/**
 * Classify error for consistent handling.
 * Tight rules: plan_limit only when error.code === "PLAN_LIMIT" (regardless of HTTP status).
 */
export function classifyError(
  err: unknown,
  status?: number
): { kind: ErrorKind; info?: PlanLimitInfo } {
  if (err instanceof Error) {
    if (err.name === "AbortError") {
      return { kind: "network" };
    }
  }

  if (status === 0) return { kind: "network" };
  if (status === 401) return { kind: "auth" };
  if (status === 403) {
    const obj = err && typeof err === "object" ? (err as Record<string, unknown>) : null;
    const code = obj?.code;
    if (typeof code === "string" && code === "PLAN_LIMIT") {
      return { kind: "plan_limit", info: normalizeToPlanLimitInfo(err, status, "proxy") };
    }
    return { kind: "auth" };
  }

  if (status !== undefined && status >= 500) return { kind: "unknown" };

  const obj = err && typeof err === "object" ? (err as Record<string, unknown>) : null;
  const code = obj?.code ?? (err && typeof err === "object" && "error" in (err as object)
    ? ((err as { error?: { code?: string } }).error?.code)
    : undefined);

  if (typeof code === "string") {
    if (code === "PLAN_LIMIT") {
      const info = normalizeToPlanLimitInfo(err, status, "proxy");
      return { kind: "plan_limit", info };
    }
    if (code === "AUTH_REQUIRED" || code === "UPSTREAM_AUTH") {
      return { kind: "auth" };
    }
  }

  const proxyErr = err as { code?: string; requestId?: string } | undefined;
  if (proxyErr && typeof proxyErr.code === "string" && proxyErr.code === "PLAN_LIMIT") {
    return { kind: "plan_limit", info: normalizeToPlanLimitInfo(err, status, "proxy") };
  }

  return { kind: "unknown" };
}
